import { FC, Fragment, useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { WearableChargingStatusResponse } from "@/api/SensorsService"
import Button, { ButtonIconPosition } from "@/components/shared/Button"
import { IconProps } from "@/components/shared/Icon"
import {
   WearableCardProps,
   WearableCardStatus,
} from "@/components/WearableCard"
import WearablesDrawer from "@/components/WearablesDrawer"
import { TestId } from "@/constants/globals"
import { AppDispatch } from "@/store"
import {
   fetchWearableChargingStatus,
   fetchWearableSensors,
   selectUnassignedWearableSensors,
} from "@/store/wearableSensorsSlice"
import { ButtonVariant } from "@/types/index"

export const POLL_INTERVAL_MS = 30 * 1000

enum WearableDrawerType {
   UNASSIGNED = "UNASSIGNED",
   CHARGING = "CHARGING",
}

interface WearableDrawerConfig {
   icon: IconProps["name"]
   title: string
   emptyMessage: string
   testId: TestId
   cards: WearableCardProps[]
}

const WearableDrawerButtons: FC = () => {
   const dispatch = useDispatch<AppDispatch>()
   const [openDrawer, setOpenDrawer] = useState<WearableDrawerType | null>(null)

   const unassignedWearables = useSelector(selectUnassignedWearableSensors)
   const [chargingWearables, setChargingWearables] = useState<
      WearableChargingStatusResponse[]
   >([])

   const fetchChargingWearables = useCallback(async () => {
      try {
         const response = await dispatch(fetchWearableChargingStatus()).unwrap()
         setChargingWearables(response.data)
      } catch (err) {
         console.error("Charging wearables data fetch failed:", err)
      }
   }, [dispatch])

   const fetchUnassignedWearables = useCallback(
      () => dispatch(fetchWearableSensors()),
      [dispatch]
   )

   useEffect(() => {
      fetchUnassignedWearables()
      fetchChargingWearables()
      const interval = setInterval(() => {
         fetchUnassignedWearables()
         fetchChargingWearables()
      }, POLL_INTERVAL_MS)

      return () => clearInterval(interval)
   }, [fetchUnassignedWearables, fetchChargingWearables])

   const drawers: Record<WearableDrawerType, WearableDrawerConfig> = useMemo(
      () => ({
         [WearableDrawerType.UNASSIGNED]: {
            icon: "unlinked",
            title: "Unassigned",
            emptyMessage: "No unassigned ReassureBands",
            testId: TestId.FLOOR_OVERVIEW_UNASSIGNED_WEARABLES_BUTTON,
            cards: unassignedWearables.map((sensor) => ({
               wearableId: sensor.id,
               inmateName: sensor.macAddress,
               status: WearableCardStatus.UNASSIGNED,
            })),
         },
         [WearableDrawerType.CHARGING]: {
            icon: "batteryCharging",
            title: "Charging",
            emptyMessage: "No ReassureBands charging",
            testId: TestId.FLOOR_OVERVIEW_CHARGING_WEARABLES_BUTTON,
            cards: chargingWearables
               .filter((wearable) => wearable.charging)
               .map((wearable) => ({
                  wearableId: wearable.id,
                  inmateName: wearable.macAddress,
                  chargeLevel:
                     wearable.batteryPercentage === null
                        ? null
                        : Math.round(wearable.batteryPercentage),
                  status: WearableCardStatus.CHARGING,
               })),
         },
      }),
      [unassignedWearables, chargingWearables]
   )

   return Object.values(WearableDrawerType).map((type) => {
      const { icon, title, emptyMessage, testId, cards } = drawers[type]
      return (
         <Fragment key={type}>
            <Button
               className="floor-overview__offline-wearables-button"
               iconName={icon}
               iconPosition={ButtonIconPosition.LEFT}
               iconSize="24"
               variant={ButtonVariant.LARGE}
               onClick={() => setOpenDrawer(type)}
               data-testid={testId}
            >
               {cards.length.toString()}
            </Button>
            <WearablesDrawer
               show={openDrawer === type}
               icon={icon}
               title={title}
               data={cards}
               emptyMessage={emptyMessage}
               onHide={() => setOpenDrawer(null)}
            />
         </Fragment>
      )
   })
}

export default WearableDrawerButtons
